import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PlatformUser } from '@/api/users/usersApi.types';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { ListSearchInput } from '@/components/admin/shared/ListSearchInput';
import { PendingInvitationsList } from '@/components/admin/shared/PendingInvitationsList';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ADMIN_LIST_PAGE_SIZE, SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useHasPlatformPermission } from '@/hooks/useHasPlatformPermission';
import { usePendingInvitationsQuery } from '@/hooks/usePendingInvitationsQuery';
import { usePlatformUsersQuery } from '@/hooks/usePlatformUsersQuery';
import { buildNameInitials } from '@/lib/nameInitials';
import { SCOPE_CODE } from '@/constants/scopes';
import { InviteUserModal } from './InviteUserModal';

export function UsersSection() {
  const { t } = useTranslation(['admin', 'common']);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MILLISECONDS);
  const [page, setPage] = useState(1);

  useEffect(
    function resetPageOnSearchChange() {
      setPage(1);
    },
    [debouncedSearch]
  );

  const platformUsersQuery = usePlatformUsersQuery(page, debouncedSearch);
  const [invitationsPage, setInvitationsPage] = useState(1);
  const invitationsQuery = usePendingInvitationsQuery(SCOPE_CODE.platform, invitationsPage);
  const canInvite = useHasPlatformPermission('invitation.create');
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const usersResult = platformUsersQuery.data;
  const pendingInvitations = invitationsQuery.data === undefined ? [] : invitationsQuery.data.items;
  const pendingInvitationsTotal =
    invitationsQuery.data === undefined ? 0 : invitationsQuery.data.totalItems;

  return (
    <div>
      {canInvite === true ? (
        <div className="flex justify-end mb-5">
          <Button
            onClick={function openInviteModal() {
              setIsInviteModalOpen(true);
            }}
          >
            <Icon name="UserPlus" />
            {t('admin:users.inviteAction')}
          </Button>
        </div>
      ) : null}

      <div className="mb-5">
        <ListSearchInput
          value={searchInput}
          onChange={setSearchInput}
          placeholder={t('admin:users.searchPlaceholder')}
        />
      </div>

      {platformUsersQuery.isLoading === true ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner className="w-7 h-7" />
        </div>
      ) : platformUsersQuery.isError === true || usersResult === undefined ? (
        <QueryErrorState
          onRetry={function retryUsers() {
            platformUsersQuery.refetch();
          }}
        />
      ) : (
        <>
          <PlatformUsersTable
            users={usersResult.items}
            hasSearch={debouncedSearch.trim().length > 0}
          />
          <ListPagination
            page={page}
            pageSize={ADMIN_LIST_PAGE_SIZE}
            totalItems={usersResult.totalItems}
            onPageChange={setPage}
          />
        </>
      )}

      {pendingInvitationsTotal > 0 ? (
        <PendingInvitationsList
          invitations={pendingInvitations}
          title={t('admin:users.pendingInvitations')}
          page={invitationsPage}
          totalItems={pendingInvitationsTotal}
          onPageChange={setInvitationsPage}
        />
      ) : null}

      {isInviteModalOpen === true ? (
        <InviteUserModal
          onClose={function closeInviteModal() {
            setIsInviteModalOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

interface PlatformUsersTableProps {
  users: PlatformUser[];
  hasSearch: boolean;
}

function PlatformUsersTable({ users, hasSearch }: PlatformUsersTableProps) {
  const { t } = useTranslation('admin');

  if (users.length === 0) {
    return (
      <Card padding="lg" shadow={false} className="text-center text-sm text-zoomuted">
        {hasSearch === true ? t('users.noResults') : t('users.emptyList')}
      </Card>
    );
  }

  return (
    <Card padding="none" className="divide-y divide-zooborder">
      {users.map(function renderUserRow(platformUser) {
        return (
          <div key={platformUser.id} className="px-4 sm:px-5 py-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-zoovpalelavender text-zooprimary flex items-center justify-center font-bold text-xs shrink-0">
              {buildNameInitials(platformUser.fullName)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-zooindigo truncate">
                {platformUser.fullName}
              </div>
              <div className="text-xs text-zoomuted truncate">{platformUser.email}</div>
            </div>
            <StatusBadge isActive={platformUser.isActive} />
          </div>
        );
      })}
    </Card>
  );
}

interface StatusBadgeProps {
  isActive: boolean;
}

function StatusBadge({ isActive }: StatusBadgeProps) {
  const { t } = useTranslation('admin');
  if (isActive === true) {
    return (
      <Badge tone="success" size="sm" className="shrink-0">
        {t('users.statusActive')}
      </Badge>
    );
  }
  return (
    <Badge tone="muted" size="sm" className="shrink-0">
      {t('users.statusInactive')}
    </Badge>
  );
}
